import type {
  AthenaAdminHasPermissionRequest,
  AthenaAdminRevokeUserSessionRequest,
  AthenaAdminRevokeUserSessionsRequest,
  AthenaAdminSuccessResponse,
  AthenaAdminHasPermissionResponse,
  AthenaApiKeyDeleteAllExpiredResponse,
  AthenaAuthCallOptions,
  AthenaAuthClientConfig,
  AthenaAuthEndpointPath,
  AthenaAuthErrorCode,
  AthenaAuthErrorDetails,
  AthenaAuthFetchCompatibleInput,
  AthenaAuthGetUserResponse,
  AthenaAuthGenericInput,
  AthenaAuthGenericQueryInput,
  AthenaAuthBindings,
  AthenaAuthEmailListQuery,
  AthenaAuthEmailListResponse,
  AthenaAuthGuardResult,
  AthenaAuthHealthResponse,
  AthenaAuthOkResponse,
  AthenaAuthLinkedAccount,
  AthenaAuthOrganizationBindings,
  AthenaAuthOrganization,
  AthenaAuthOrganizationCheckSlugRequest,
  AthenaAuthOrganizationCreateRequest,
  AthenaAuthOrganizationDeleteRequest,
  AthenaAuthOrganizationGetFullQuery,
  AthenaAuthOrganizationGetInvitationQuery,
  AthenaAuthOrganizationInvitation,
  AthenaAuthOrganizationInvitationActionRequest,
  AthenaAuthOrganizationInviteMemberRequest,
  AthenaAuthOrganizationLeaveRequest,
  AthenaAuthOrganizationListInvitationsQuery,
  AthenaAuthOrganizationListMembersQuery,
  AthenaAuthOrganizationListUserInvitationsQuery,
  AthenaAuthOrganizationMember,
  AthenaAuthOrganizationRemoveMemberRequest,
  AthenaAuthOrganizationSetActiveRequest,
  AthenaAuthOrganizationUpdateMemberRoleRequest,
  AthenaAuthOrganizationUpdateRequest,
  AthenaAuthMethod,
  AthenaAuthQueryValue,
  AthenaAuthRequestInput,
  AthenaAuthReactEmailRenderInput,
  AthenaAuthResult,
  AthenaAuthSdkClient,
  AthenaAuthSession,
  AthenaAuthSessionResponse,
  AthenaAuthSignInResponse,
  AthenaAuthSignOutResponse,
  AthenaAuthSocialRedirectResponse,
  AthenaAuthStatusResponse,
  AthenaAuthUser,
  AthenaChangeEmailRequest,
  AthenaChangePasswordRequest,
  AthenaDeleteUserCallbackRequest,
  AthenaDeleteUserRequest,
  AthenaDeleteUserResponse,
  AthenaEmailSignInRequest,
  AthenaEmailSignUpRequest,
  AthenaForgetPasswordRequest,
  AthenaLinkSocialRequest,
  AthenaOAuthAccountTokenRequest,
  AthenaOAuthTokenBundle,
  AthenaResetPasswordRequest,
  AthenaSendVerificationEmailRequest,
  AthenaSocialSignInRequest,
  AthenaTwoFactorGenerateBackupCodesRequest,
  AthenaTwoFactorGenerateBackupCodesResponse,
  AthenaUnlinkAccountRequest,
  AthenaUpdateUserRequest,
  AthenaVerifyEmailRequest,
  AthenaUsernameSignInRequest,
} from './types.ts'
import { assertAthenaAuthTemplateVariables } from './limits.ts'
import { resolveReactEmailPayloadFields } from './react-email.ts'
import { buildSdkHeaderValue } from '../sdk-version.ts'
import { buildServiceRequestHeaders } from '../utils/athena-request-headers.ts'

const DEFAULT_AUTH_BASE_URL = 'http://localhost:3001/api/auth'
const SDK_NAME = 'xylex-group/athena-auth'
const SDK_HEADER_VALUE = buildSdkHeaderValue(SDK_NAME)

type AuthRequestContext = {
  endpoint: AthenaAuthEndpointPath
  method: AthenaAuthMethod
}

type InternalErrorInput = AuthRequestContext & {
  code: AthenaAuthErrorCode
  status: number
  message: string
  requestId?: string
  hint?: string
  cause?: string
}

function normalizeBaseUrl(baseUrl?: string): string {
  return (baseUrl ?? DEFAULT_AUTH_BASE_URL).replace(/\/$/, '')
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function parseResponseBody(rawText: string, contentType: string | null) {
  if (!rawText) {
    return { parsed: null as unknown, parseFailed: false }
  }

  const contentTypeSuggestsJson =
    contentType?.toLowerCase().includes('application/json') ?? false
  const looksJson =
    contentTypeSuggestsJson || rawText.startsWith('{') || rawText.startsWith('[')

  if (!looksJson) {
    return { parsed: rawText as unknown, parseFailed: false }
  }

  try {
    return { parsed: JSON.parse(rawText) as unknown, parseFailed: false }
  } catch {
    return { parsed: rawText as unknown, parseFailed: true }
  }
}

function resolveRequestId(headers: Headers): string | undefined {
  return (
    headers.get('x-request-id') ??
    headers.get('x-correlation-id') ??
    headers.get('x-athena-request-id') ??
    undefined
  )
}

function resolveErrorMessage(payload: unknown, fallback: string) {
  if (isRecord(payload)) {
    const messageCandidates = [payload.error, payload.message, payload.details]
    for (const candidate of messageCandidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim()
      }
    }
  }

  if (typeof payload === 'string' && payload.trim().length > 0) {
    return payload.trim()
  }

  return fallback
}

function toErrorDetails(input: InternalErrorInput): AthenaAuthErrorDetails {
  return {
    code: input.code,
    message: input.message,
    status: input.status,
    endpoint: input.endpoint,
    method: input.method,
    requestId: input.requestId,
    hint: input.hint,
    cause: input.cause,
  }
}

function mergeCallOptions(
  base?: AthenaAuthCallOptions,
  override?: AthenaAuthCallOptions,
): AthenaAuthCallOptions | undefined {
  if (!base && !override) return undefined
  return {
    ...base,
    ...override,
    headers: {
      ...(base?.headers ?? {}),
      ...(override?.headers ?? {}),
    },
  }
}

function copyDefinedField(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
  targetKey: string,
  sourceKey: string,
): void {
  if (!(sourceKey in source)) {
    return
  }
  const value = source[sourceKey]
  if (value !== undefined) {
    target[targetKey] = value
  }
}

function normalizeEmailTemplateAttachmentsValue(value: unknown): unknown {
  if (typeof value === 'string' || value == null) {
    return value
  }

  if (Array.isArray(value)) {
    return value.map(item => normalizeEmailTemplateAttachmentsValue(item))
  }

  if (typeof value !== 'object') {
    return value
  }

  const attachment = { ...(value as Record<string, unknown>) }
  copyDefinedField(attachment, attachment, 'file_url', 'fileUrl')
  delete attachment.fileUrl
  return attachment
}

function normalizeAdminEmailTemplatePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...payload }

  copyDefinedField(normalized, payload, 'template_key', 'templateKey')
  copyDefinedField(normalized, payload, 'event_type', 'eventType')
  copyDefinedField(normalized, payload, 'subject_template', 'subjectTemplate')
  copyDefinedField(normalized, payload, 'text_template', 'textTemplate')
  copyDefinedField(normalized, payload, 'html_template', 'htmlTemplate')
  copyDefinedField(normalized, payload, 'variable_bindings', 'variableBindings')
  copyDefinedField(normalized, payload, 'attachment_failure_mode', 'attachmentFailureMode')
  copyDefinedField(normalized, payload, 'is_active', 'isActive')

  if (Object.hasOwn(payload, 'attachments')) {
    normalized.attachments = normalizeEmailTemplateAttachmentsValue(payload.attachments)
  }

  delete normalized.templateKey
  delete normalized.eventType
  delete normalized.subjectTemplate
  delete normalized.textTemplate
  delete normalized.htmlTemplate
  delete normalized.variableBindings
  delete normalized.attachmentFailureMode
  delete normalized.isActive

  return normalized
}

function toReactEmailTemplateCompatibilityInput<TInput extends AthenaAuthFetchCompatibleInput & {
  react?: AthenaAuthReactEmailRenderInput
}>(input: TInput): TInput {
  const payload = input as Record<string, unknown>
  const compatibility = { ...payload }

  copyDefinedField(compatibility, payload, 'templateKey', 'template_key')
  copyDefinedField(compatibility, payload, 'eventType', 'event_type')
  copyDefinedField(compatibility, payload, 'subjectTemplate', 'subject_template')
  copyDefinedField(compatibility, payload, 'textTemplate', 'text_template')
  copyDefinedField(compatibility, payload, 'htmlTemplate', 'html_template')
  copyDefinedField(compatibility, payload, 'variableBindings', 'variable_bindings')
  copyDefinedField(compatibility, payload, 'attachmentFailureMode', 'attachment_failure_mode')
  copyDefinedField(compatibility, payload, 'isActive', 'is_active')

  return compatibility as unknown as TInput
}

function normalizeAdminEmailTemplateSendPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...payload }

  copyDefinedField(normalized, payload, 'template_id', 'templateId')
  copyDefinedField(normalized, payload, 'recipient_email', 'recipientEmail')
  copyDefinedField(normalized, payload, 'render_variables', 'renderVariables')
  copyDefinedField(normalized, payload, 'user_id', 'userId')
  copyDefinedField(normalized, payload, 'organization_id', 'organizationId')
  copyDefinedField(normalized, payload, 'session_token', 'sessionToken')
  copyDefinedField(normalized, payload, 'attachment_failure_mode', 'attachmentFailureMode')

  if (Object.hasOwn(payload, 'attachments')) {
    normalized.attachments = normalizeEmailTemplateAttachmentsValue(payload.attachments)
  }

  delete normalized.templateId
  delete normalized.recipientEmail
  delete normalized.renderVariables
  delete normalized.userId
  delete normalized.organizationId
  delete normalized.sessionToken
  delete normalized.attachmentFailureMode

  return normalized
}

function toSessionGuardFailure(
  sessionResult: AthenaAuthResult<AthenaAuthSessionResponse>,
): AthenaAuthGuardResult {
  if (sessionResult.status === 401 || sessionResult.data == null) {
    return {
      ok: false,
      reason: 'unauthorized',
      status: 401,
      error: sessionResult.error ?? 'Unauthorized',
      sessionResult,
    }
  }

  return {
    ok: false,
    reason: 'upstream_error',
    status: sessionResult.status,
    error: sessionResult.error ?? 'Failed to resolve current session',
    sessionResult,
  }
}

function toPermissionGuardFailure(
  permissionResult: AthenaAuthResult<AthenaAdminHasPermissionResponse>,
  sessionResult: AthenaAuthResult<AthenaAuthSessionResponse>,
): AthenaAuthGuardResult {
  if (permissionResult.status === 401) {
    return {
      ok: false,
      reason: 'unauthorized',
      status: 401,
      error: permissionResult.error ?? 'Unauthorized',
      sessionResult,
      permissionResult,
    }
  }

  if (permissionResult.status === 403) {
    return {
      ok: false,
      reason: 'forbidden',
      status: 403,
      error: permissionResult.error ?? 'Forbidden',
      sessionResult,
      permissionResult,
    }
  }

  return {
    ok: false,
    reason: 'upstream_error',
    status: permissionResult.status,
    error: permissionResult.error ?? 'Failed to resolve permission check',
    sessionResult,
    permissionResult,
  }
}

function extractFetchOptions<T extends AthenaAuthFetchCompatibleInput | undefined>(input: T) {
  if (!input) {
    return {
      payload: undefined,
      fetchOptions: undefined,
    }
  }

  const { fetchOptions, ...rest } = input
  const hasPayloadKeys = Object.keys(rest).length > 0
  return {
    payload: hasPayloadKeys ? rest : undefined,
    fetchOptions,
  }
}

function buildHeaders(
  config: AthenaAuthClientConfig,
  options?: AthenaAuthCallOptions,
): Record<string, string> {
  return buildServiceRequestHeaders('auth', SDK_HEADER_VALUE, config, options)
}

function appendQueryParam(searchParams: URLSearchParams, key: string, value: AthenaAuthQueryValue) {
  if (value === undefined || value === null) return
  if (Array.isArray(value)) {
    value.forEach(item => {
      searchParams.append(key, String(item))
    })
    return
  }
  searchParams.append(key, String(value))
}

function buildRequestUrl(
  baseUrl: string,
  endpoint: AthenaAuthEndpointPath,
  query?: Record<string, AthenaAuthQueryValue>,
) {
  const url = `${baseUrl}${endpoint}`
  if (!query || Object.keys(query).length === 0) return url
  const searchParams = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => appendQueryParam(searchParams, key, value))
  const queryText = searchParams.toString()
  return queryText ? `${url}?${queryText}` : url
}

function inferDefaultMethod(endpoint: AthenaAuthEndpointPath): AthenaAuthMethod {
  if (endpoint.startsWith('/reset-password/')) {
    return 'GET'
  }

  switch (endpoint) {
    case '/get-session':
    case '/list-sessions':
    case '/verify-email':
    case '/change-email/verify':
    case '/delete-user/verify':
    case '/email-list':
    case '/email/list':
    case '/delete-user/callback':
    case '/list-accounts':
    case '/passkey/generate-register-options':
    case '/passkey/list-user-passkeys':
    case '/.well-known/webauthn':
    case '/admin/list-users':
    case '/admin/athena-client/list':
    case '/admin/audit-log/list':
    case '/admin/email/get':
    case '/admin/email-failure/list':
    case '/admin/email-failure/get':
    case '/admin/email-template/get':
    case '/admin/email-template/list':
    case '/admin/email/list':
    case '/api-key/get':
    case '/api-key/list':
    case '/organization/get-full-organization':
    case '/organization/list':
    case '/organization/get-invitation':
    case '/organization/list-invitations':
    case '/organization/list-user-invitations':
    case '/organization/list-members':
    case '/organization/get-active-member':
    case '/health':
    case '/ok':
    case '/error':
      return 'GET'
    default:
      return 'POST'
  }
}

async function callAuthEndpoint<T>(
  config: AthenaAuthClientConfig,
  context: AuthRequestContext,
  body?: unknown,
  query?: Record<string, AthenaAuthQueryValue>,
  options?: AthenaAuthCallOptions,
): Promise<AthenaAuthResult<T>> {
  const baseUrl = normalizeBaseUrl(options?.baseUrl ?? config.baseUrl)
  const url = buildRequestUrl(baseUrl, context.endpoint, query)
  const headers = buildHeaders(config, options)
  const credentials = options?.credentials ?? config.credentials ?? 'include'
  const requestInit: RequestInit = {
    method: context.method,
    headers,
    credentials,
    signal: options?.signal,
  }

  if (context.method !== 'GET') {
    requestInit.body = JSON.stringify(body ?? {})
  }

  const fetcher = config.fetch ?? globalThis.fetch
  if (!fetcher) {
    const details = toErrorDetails({
      code: 'UNKNOWN_ERROR',
      message: 'No fetch implementation available for auth client',
      status: 0,
      endpoint: context.endpoint,
      method: context.method,
      hint: 'Use Node 18+ or provide `fetch` via createClient(..., { auth: { fetch } }) or createAuthClient({ fetch })',
    })
    return {
      ok: false,
      status: 0,
      data: null,
      error: details.message,
      errorDetails: details,
      raw: null,
    }
  }

  try {
    const response = await fetcher(url, requestInit)
    const rawText = await response.text()
    const requestId = resolveRequestId(response.headers)
    const parsedBody = parseResponseBody(rawText ?? '', response.headers.get('content-type'))

    if (parsedBody.parseFailed) {
      const details = toErrorDetails({
        code: 'INVALID_JSON',
        message: 'Auth server returned malformed JSON',
        status: response.status,
        endpoint: context.endpoint,
        method: context.method,
        requestId,
        hint: 'Verify the auth endpoint response body is valid JSON.',
        cause: rawText.slice(0, 300),
      })
      return {
        ok: false,
        status: response.status,
        data: null,
        error: details.message,
        errorDetails: details,
        raw: parsedBody.parsed,
      }
    }

    const parsed = parsedBody.parsed

    if (!response.ok) {
      const details = toErrorDetails({
        code: 'HTTP_ERROR',
        message: resolveErrorMessage(
          parsed,
          `Auth endpoint ${context.method} ${context.endpoint} failed with status ${response.status}`,
        ),
        status: response.status,
        endpoint: context.endpoint,
        method: context.method,
        requestId,
      })
      return {
        ok: false,
        status: response.status,
        data: null,
        error: details.message,
        errorDetails: details,
        raw: parsed,
      }
    }

    return {
      ok: true,
      status: response.status,
      data: (parsed as T) ?? null,
      error: null,
      errorDetails: null,
      raw: parsed,
    }
  } catch (callError) {
    const message = callError instanceof Error ? callError.message : String(callError)
    const details = toErrorDetails({
      code: 'NETWORK_ERROR',
      message: `Network error while calling ${context.method} ${context.endpoint}: ${message}`,
      status: 0,
      endpoint: context.endpoint,
      method: context.method,
      cause: message,
      hint: 'Check auth server URL, DNS, and network reachability.',
    })
    return {
      ok: false,
      status: 0,
      data: null,
      error: details.message,
      errorDetails: details,
      raw: null,
    }
  }
}

function executePostWithCompatibleInput<TPayload extends AthenaAuthFetchCompatibleInput, TResult>(
  config: AthenaAuthClientConfig,
  context: AuthRequestContext,
  input: TPayload,
  options?: AthenaAuthCallOptions,
) {
  const { payload, fetchOptions } = extractFetchOptions(input)
  const mergedOptions = mergeCallOptions(fetchOptions, options)
  return callAuthEndpoint<TResult>(config, context, payload ?? {}, undefined, mergedOptions)
}

function executePostWithOptionalInput<TResult>(
  config: AthenaAuthClientConfig,
  context: AuthRequestContext,
  input?: AthenaAuthFetchCompatibleInput,
  options?: AthenaAuthCallOptions,
) {
  const { fetchOptions } = extractFetchOptions(input)
  const mergedOptions = mergeCallOptions(fetchOptions, options)
  return callAuthEndpoint<TResult>(config, context, {}, undefined, mergedOptions)
}

function executeGetWithCompatibleInput<TResult>(
  config: AthenaAuthClientConfig,
  context: AuthRequestContext,
  input?: AthenaAuthFetchCompatibleInput,
  options?: AthenaAuthCallOptions,
) {
  const { fetchOptions } = extractFetchOptions(input)
  const mergedOptions = mergeCallOptions(fetchOptions, options)
  return callAuthEndpoint<TResult>(config, context, undefined, undefined, mergedOptions)
}

function executeGetWithQueryCompatibleInput<
  TQuery extends object,
  TResult,
>(
  config: AthenaAuthClientConfig,
  context: AuthRequestContext,
  input?: { query?: TQuery } & AthenaAuthFetchCompatibleInput,
  options?: AthenaAuthCallOptions,
) {
  const { payload, fetchOptions } = extractFetchOptions(input)
  const mergedOptions = mergeCallOptions(fetchOptions, options)
  const query = (payload as { query?: Record<string, AthenaAuthQueryValue> } | undefined)?.query
  return callAuthEndpoint<TResult>(
    config,
    context,
    undefined,
    query,
    mergedOptions,
  )
}

/**
 * @deprecated Prefer `createClient(...).auth` from `@xylex-group/athena`.
 */
export function createAuthClient(config: AthenaAuthClientConfig = {}): AthenaAuthSdkClient {
  const normalizedBaseUrl = normalizeBaseUrl(config.baseUrl)
  const resolvedConfig: AthenaAuthClientConfig = {
    ...config,
    baseUrl: normalizedBaseUrl,
  }

  const request = <T = unknown>(
    input: AthenaAuthRequestInput,
    options?: AthenaAuthCallOptions,
  ): Promise<AthenaAuthResult<T>> => {
    const method = input.method ?? (input.body !== undefined ? 'POST' : inferDefaultMethod(input.endpoint))
    const mergedOptions = mergeCallOptions(input.fetchOptions, options)
    return callAuthEndpoint<T>(
      resolvedConfig,
      { endpoint: input.endpoint, method },
      input.body,
      input.query,
      mergedOptions,
    )
  }

  const postGeneric = <T = unknown>(
    endpoint: AthenaAuthEndpointPath,
    input?: (AthenaAuthFetchCompatibleInput & object),
    options?: AthenaAuthCallOptions,
  ) => {
    const { payload, fetchOptions } = extractFetchOptions(input)
    return request<T>(
      {
        endpoint,
        method: 'POST',
        body: payload ?? {},
        fetchOptions,
      },
      options,
    )
  }

  const getGeneric = <T = unknown>(
    endpoint: AthenaAuthEndpointPath,
    input?: AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) => {
    const { fetchOptions } = extractFetchOptions(input)
    return request<T>(
      {
        endpoint,
        method: 'GET',
        fetchOptions,
      },
      options,
    )
  }

  const getWithQuery = <
    T = unknown,
    TQuery extends object = Record<string, AthenaAuthQueryValue>,
  >(
    endpoint: AthenaAuthEndpointPath,
    input?: AthenaAuthFetchCompatibleInput & {
      query?: TQuery
    },
    options?: AthenaAuthCallOptions,
  ) => {
    const { payload, fetchOptions } = extractFetchOptions(input)
    const query = (payload as { query?: TQuery } | undefined)?.query as
      | Record<string, AthenaAuthQueryValue>
      | undefined
    return request<T>(
      {
        endpoint,
        method: 'GET',
        query,
        fetchOptions,
      },
      options,
    )
  }

  const withReactEmailRoute = (route: AthenaAuthEndpointPath) => ({
    ...resolvedConfig.reactEmail,
    route,
  })

  const resolveAdminEmailPayload = <TInput extends AthenaAuthFetchCompatibleInput & {
    react?: AthenaAuthReactEmailRenderInput
  }>(
    route: '/admin/email/create' | '/admin/email/update',
    input: TInput,
  ) =>
    resolveReactEmailPayloadFields(input, {
      htmlField: 'htmlBody',
      textField: 'textBody',
    }, withReactEmailRoute(route))

  const resolveAdminEmailTemplatePayload = <TInput extends AthenaAuthFetchCompatibleInput & {
    react?: AthenaAuthReactEmailRenderInput
  }>(
    route: '/admin/email-template/create' | '/admin/email-template/update',
    input: TInput,
  ) =>
    resolveReactEmailPayloadFields(toReactEmailTemplateCompatibilityInput(input), {
      htmlField: 'htmlTemplate',
      textField: 'textTemplate',
      variablesField: 'variables',
    }, withReactEmailRoute(route)).then(payload => {
      const normalizedPayload = normalizeAdminEmailTemplatePayload(payload as Record<string, unknown>)
      if ('variables' in payload && payload.variables !== undefined && payload.variables !== null) {
        assertAthenaAuthTemplateVariables(
          payload.variables,
          `${route} variables`,
        )
      }
      return normalizedPayload
    })

  const requireSession: AthenaAuthBindings['requireSession'] = async (input, options) => {
    const sessionInput = input?.fetchOptions
      ? { fetchOptions: input.fetchOptions }
      : undefined
    const sessionResult = await getGeneric<AthenaAuthSessionResponse>(
      '/get-session',
      sessionInput,
      options,
    )

    if (!sessionResult.ok || sessionResult.data == null) {
      return toSessionGuardFailure(sessionResult)
    }

    return {
      ok: true,
      session: sessionResult.data,
    }
  }

  const getUser: AthenaAuthBindings['getUser'] = async (input, options) => {
    const sessionResult = await getGeneric<AthenaAuthSessionResponse>(
      '/get-session',
      input,
      options,
    )

    if (!sessionResult.ok) {
      return {
        ...sessionResult,
        data: null,
      }
    }

    return {
      ...sessionResult,
      data: {
        user: sessionResult.data?.user ?? null,
      } satisfies AthenaAuthGetUserResponse,
    }
  }

  const requirePermission = async (
    endpoint: '/admin/has-permission' | '/organization/has-permission',
    input: AthenaAdminHasPermissionRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ): Promise<AthenaAuthGuardResult> => {
    const sessionGuard = await requireSession(
      input?.fetchOptions ? { fetchOptions: input.fetchOptions } : undefined,
      options,
    )

    if (!sessionGuard.ok) {
      return sessionGuard
    }

    const permissionResult = await postGeneric<AthenaAdminHasPermissionResponse>(
      endpoint,
      input,
      options,
    )

    if (!permissionResult.ok) {
      return toPermissionGuardFailure(permissionResult, {
        ok: true,
        status: 200,
        data: sessionGuard.session,
        error: null,
        errorDetails: null,
        raw: sessionGuard.session,
      })
    }

    if (!permissionResult.data?.success) {
      return {
        ok: false,
        reason: 'forbidden',
        status: 403,
        error: permissionResult.data?.error ?? 'Forbidden',
        sessionResult: {
          ok: true,
          status: 200,
          data: sessionGuard.session,
          error: null,
          errorDetails: null,
          raw: sessionGuard.session,
        },
        permissionResult,
      }
    }

    return sessionGuard
  }

  const listUserEmailsWithFallback: AthenaAuthBindings['user']['email']['list'] = async (input, options) => {
    const primary = await getWithQuery<AthenaAuthEmailListResponse, AthenaAuthEmailListQuery>(
      '/email/list',
      input,
      options,
    )
    if (primary.ok || primary.status !== 404 || primary.errorDetails?.code !== 'HTTP_ERROR') {
      return primary
    }
    return getWithQuery<AthenaAuthEmailListResponse, AthenaAuthEmailListQuery>(
      '/email-list',
      input,
      options,
    )
  }

  const healthWithFallback: AthenaAuthBindings['health'] = async (input, options) => {
    const primary = await getGeneric<AthenaAuthHealthResponse>('/health', input, options)
    if (primary.ok || primary.status !== 404 || primary.errorDetails?.code !== 'HTTP_ERROR') {
      return primary
    }

    const fallback = await getGeneric<AthenaAuthOkResponse>('/ok', input, options)
    if (!fallback.ok) {
      return {
        ...fallback,
        data: null,
      }
    }

    const fallbackStatus =
      isRecord(fallback.data) && typeof fallback.data.ok === 'boolean'
        ? (fallback.data.ok ? 'ok' : 'error')
        : 'ok'

    return {
      ...fallback,
      data: {
        status: fallbackStatus,
      },
    }
  }

  const signOut = (
    input?: AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) =>
    executePostWithOptionalInput<AthenaAuthSignOutResponse>(
      resolvedConfig,
      { endpoint: '/sign-out', method: 'POST' },
      input,
      options,
    )

  const revokeSessions = (
    input?: AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) =>
    executePostWithOptionalInput<AthenaAuthStatusResponse>(
      resolvedConfig,
      { endpoint: '/revoke-sessions', method: 'POST' },
      input,
      options,
    )

  const revokeOtherSessions = (
    input?: AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) =>
    executePostWithOptionalInput<AthenaAuthStatusResponse>(
      resolvedConfig,
      { endpoint: '/revoke-other-sessions', method: 'POST' },
      input,
      options,
    )

  const revokeSession = (
    input: { token: string } & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) =>
    executePostWithCompatibleInput<typeof input, AthenaAuthStatusResponse>(
      resolvedConfig,
      { endpoint: '/revoke-session', method: 'POST' },
      input,
      options,
    )

  const deleteUser = (
    input?: AthenaDeleteUserRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) => {
    const { payload, fetchOptions } = extractFetchOptions(input)
    const mergedOptions = mergeCallOptions(fetchOptions, options)
    return callAuthEndpoint<AthenaDeleteUserResponse>(
      resolvedConfig,
      { endpoint: '/delete-user', method: 'POST' },
      payload ?? {},
      undefined,
      mergedOptions,
    )
  }

  const deleteUserCallback = (
    input?: AthenaDeleteUserCallbackRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) => {
    const { payload, fetchOptions } = extractFetchOptions(input)
    const mergedOptions = mergeCallOptions(fetchOptions, options)
    const query = (payload ?? {}) as AthenaDeleteUserCallbackRequest
    return callAuthEndpoint<AthenaDeleteUserResponse>(
      resolvedConfig,
      { endpoint: '/delete-user/callback', method: 'GET' },
      undefined,
      {
        token: query.token,
        callbackURL: query.callbackURL,
      },
      mergedOptions,
    )
  }

  const resolveResetPasswordToken = (
    input: { token: string; callbackURL?: string } & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions,
  ) => {
    const { payload, fetchOptions } = extractFetchOptions(input)
    const mergedOptions = mergeCallOptions(fetchOptions, options)
    const query = payload as { token?: string; callbackURL?: string } | undefined
    const token = query?.token?.trim()
    if (!token) {
      throw new Error('resolveResetPasswordToken requires a non-empty token')
    }
    const endpoint = `/reset-password/${encodeURIComponent(token)}` as AthenaAuthEndpointPath
    return callAuthEndpoint<{ token?: string }>(
      resolvedConfig,
      { endpoint, method: 'GET' },
      undefined,
      query?.callbackURL ? { callbackURL: query.callbackURL } : undefined,
      mergedOptions,
    )
  }

  const organization: AthenaAuthOrganizationBindings = {
    create: (input, options) =>
      executePostWithCompatibleInput<
        AthenaAuthOrganizationCreateRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthOrganization
      >(
        resolvedConfig,
        { endpoint: '/organization/create', method: 'POST' },
        input,
        options,
      ),
    update: (input, options) =>
      executePostWithCompatibleInput<
        AthenaAuthOrganizationUpdateRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthOrganization
      >(
        resolvedConfig,
        { endpoint: '/organization/update', method: 'POST' },
        input,
        options,
      ),
    delete: (input, options) =>
      executePostWithCompatibleInput<
        AthenaAuthOrganizationDeleteRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/organization/delete', method: 'POST' },
        input,
        options,
      ),
    setActive: (input, options) =>
      executePostWithCompatibleInput<
        AthenaAuthOrganizationSetActiveRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/organization/set-active', method: 'POST' },
        input,
        options,
      ),
    list: (input, options) =>
      getGeneric<AthenaAuthOrganization[]>(
        '/organization/list',
        input,
        options,
      ),
    getFull: (input, options) =>
      executeGetWithQueryCompatibleInput<AthenaAuthOrganizationGetFullQuery, {
        organization: AthenaAuthOrganization
        members?: AthenaAuthOrganizationMember[]
        invitations?: AthenaAuthOrganizationInvitation[]
      }>(
        resolvedConfig,
        { endpoint: '/organization/get-full-organization', method: 'GET' },
        input,
        options,
      ),
    checkSlug: (input, options) =>
      executePostWithCompatibleInput<
        AthenaAuthOrganizationCheckSlugRequest & AthenaAuthFetchCompatibleInput,
        { available: boolean }
      >(
        resolvedConfig,
        { endpoint: '/organization/check-slug', method: 'POST' },
        input,
        options,
      ),
    leave: (input, options) =>
      executePostWithCompatibleInput<
        AthenaAuthOrganizationLeaveRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/organization/leave', method: 'POST' },
        input,
        options,
      ),
    listUserInvitations: (input, options) =>
      executeGetWithQueryCompatibleInput<
        AthenaAuthOrganizationListUserInvitationsQuery,
        AthenaAuthOrganizationInvitation[]
      >(
        resolvedConfig,
        { endpoint: '/organization/list-user-invitations', method: 'GET' },
        input,
        options,
      ),
    hasPermission: (input, options) =>
      postGeneric<AthenaAdminHasPermissionResponse>(
        '/organization/has-permission',
        input,
        options,
      ),
    requirePermission: (input, options) =>
      requirePermission('/organization/has-permission', input, options),
    invitation: {
      cancel: (input, options) =>
        executePostWithCompatibleInput<
          AthenaAuthOrganizationInvitationActionRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthStatusResponse
        >(
          resolvedConfig,
          { endpoint: '/organization/cancel-invitation', method: 'POST' },
          input,
          options,
        ),
      accept: (input, options) =>
        executePostWithCompatibleInput<
          AthenaAuthOrganizationInvitationActionRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthStatusResponse
        >(
          resolvedConfig,
          { endpoint: '/organization/accept-invitation', method: 'POST' },
          input,
          options,
        ),
      get: (input, options) =>
        executeGetWithQueryCompatibleInput<
          AthenaAuthOrganizationGetInvitationQuery,
          AthenaAuthOrganizationInvitation
        >(
          resolvedConfig,
          { endpoint: '/organization/get-invitation', method: 'GET' },
          input,
          options,
        ),
      reject: (input, options) =>
        executePostWithCompatibleInput<
          AthenaAuthOrganizationInvitationActionRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthStatusResponse
        >(
          resolvedConfig,
          { endpoint: '/organization/reject-invitation', method: 'POST' },
          input,
          options,
        ),
      list: (input, options) =>
        executeGetWithQueryCompatibleInput<
          AthenaAuthOrganizationListInvitationsQuery,
          AthenaAuthOrganizationInvitation[]
        >(
          resolvedConfig,
          { endpoint: '/organization/list-invitations', method: 'GET' },
          input,
          options,
        ),
    },
    member: {
      remove: (input, options) =>
        executePostWithCompatibleInput<
          AthenaAuthOrganizationRemoveMemberRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthStatusResponse
        >(
          resolvedConfig,
          { endpoint: '/organization/remove-member', method: 'POST' },
          input,
          options,
        ),
      updateRole: (input, options) =>
        executePostWithCompatibleInput<
          AthenaAuthOrganizationUpdateMemberRoleRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthStatusResponse
        >(
          resolvedConfig,
          { endpoint: '/organization/update-member-role', method: 'POST' },
          input,
          options,
        ),
      invite: (input, options) =>
        executePostWithCompatibleInput<
          AthenaAuthOrganizationInviteMemberRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthOrganizationInvitation
        >(
          resolvedConfig,
          { endpoint: '/organization/invite-member', method: 'POST' },
          input,
          options,
        ),
      getActive: (input, options) =>
        executeGetWithCompatibleInput<AthenaAuthOrganizationMember>(
          resolvedConfig,
          { endpoint: '/organization/get-active-member', method: 'GET' },
          input,
          options,
        ),
      list: (input, options) =>
        executeGetWithQueryCompatibleInput<
          AthenaAuthOrganizationListMembersQuery,
          AthenaAuthOrganizationMember[]
        >(
          resolvedConfig,
          { endpoint: '/organization/list-members', method: 'GET' },
          input,
          options,
        ),
    },
  }

  const authResetPassword = Object.assign(
    (
      input: AthenaResetPasswordRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaResetPasswordRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/reset-password', method: 'POST' },
        input,
        options,
      ),
    {
      token: resolveResetPasswordToken,
    },
  )

  const sessionRevokeBinding: AthenaAuthBindings['session']['revoke'] = (
    input,
    options,
  ) => {
    if (Array.isArray(input)) {
      if (input.length === 0) {
        throw new Error('session.revoke requires at least one session token')
      }
      if (input.length === 1) {
        return revokeSession(input[0], options)
      }
      return revokeSessions(undefined, options)
    }

    const parsed = input as AthenaAuthGenericInput & {
      token?: string
      tokens?: string[]
    }
    const tokens = Array.isArray(parsed.tokens)
      ? parsed.tokens.filter(token => token.trim().length > 0)
      : undefined

    if (tokens && tokens.length > 1) {
      return revokeSessions(
        parsed.fetchOptions ? { fetchOptions: parsed.fetchOptions } : undefined,
        options,
      )
    }

    if (tokens && tokens.length === 1) {
      return revokeSession(
        { token: tokens[0], fetchOptions: parsed.fetchOptions },
        options,
      )
    }

    const token = parsed.token?.trim()
    if (!token) {
      throw new Error('session.revoke requires a non-empty token or a non-empty token list')
    }

    return revokeSession(
      {
        token,
        fetchOptions: parsed.fetchOptions,
      },
      options,
    )
  }

  const adminUserSessionRevokeBinding: AthenaAuthBindings['admin']['user']['session']['revoke'] = (
    input,
    options,
  ) => {
    const requireUserId = (userId: string | undefined): string => {
      const trimmed = String(userId ?? '').trim()
      if (!trimmed) {
        throw new Error('admin.user.session.revoke requires a non-empty userId')
      }
      return trimmed
    }

    const requireSinglePluralUserId = (
      sessions: AthenaAdminRevokeUserSessionRequest[],
    ): AthenaAdminRevokeUserSessionsRequest => {
      const uniqueUserIds = [...new Set(sessions.map(session => requireUserId(session.userId)))]
      if (uniqueUserIds.length !== 1) {
        throw new Error('admin.user.session.revoke requires the same userId across plural payloads')
      }
      return { userId: uniqueUserIds[0] }
    }

    if (Array.isArray(input)) {
      if (input.length === 0) {
        throw new Error('admin.user.session.revoke requires at least one payload item')
      }
      if (input.length === 1) {
        return postGeneric<AthenaAdminSuccessResponse>(
          '/admin/revoke-user-session',
          {
            ...input[0],
            userId: requireUserId(input[0].userId),
          } as AthenaAuthGenericInput,
          options,
        )
      }
      return postGeneric<AthenaAdminSuccessResponse>(
        '/admin/revoke-user-sessions',
        requireSinglePluralUserId(input),
        options,
      )
    }

    const parsed = input as AthenaAuthGenericInput & {
      sessions?: AthenaAdminRevokeUserSessionRequest[]
      sessionToken?: string
      userId?: string
    }
    const sessions = parsed.sessions

    if (sessions && sessions.length === 0) {
      throw new Error('admin.user.session.revoke requires at least one payload item')
    }

    if (sessions && sessions.length === 1) {
      return postGeneric<AthenaAdminSuccessResponse>(
        '/admin/revoke-user-session',
        {
          ...sessions[0],
          userId: requireUserId(sessions[0].userId),
          fetchOptions: parsed.fetchOptions,
        } as AthenaAuthGenericInput,
        options,
      )
    }

    if (sessions && sessions.length > 1) {
      return postGeneric<AthenaAdminSuccessResponse>(
        '/admin/revoke-user-sessions',
        {
          ...requireSinglePluralUserId(sessions),
          fetchOptions: parsed.fetchOptions,
        } as AthenaAuthGenericInput,
        options,
      )
    }

    const normalizedUserId = requireUserId(parsed.userId)

    if (!parsed.sessionToken) {
      return postGeneric<AthenaAdminSuccessResponse>(
        '/admin/revoke-user-sessions',
        {
          userId: normalizedUserId,
          fetchOptions: parsed.fetchOptions,
        } as AthenaAuthGenericInput,
        options,
      )
    }
    return postGeneric<AthenaAdminSuccessResponse>(
      '/admin/revoke-user-session',
      {
        ...parsed,
        userId: normalizedUserId,
      } as AthenaAuthGenericInput,
      options,
    )
  }

  const auth: AthenaAuthBindings = {
    getSession: (input, options) => getGeneric('/get-session', input, options),
    getUser,
    requireSession,
    signOut,
    forgetPassword: (input, options) => postGeneric('/forget-password', input, options),
    resetPassword: authResetPassword,
    setPassword: (input, options) => postGeneric('/set-password', input, options),
    verifyEmail: (input, options) => {
      const queryInput: AthenaAuthGenericQueryInput = {
        query: {
          token: input.token,
          callbackURL: input.callbackURL,
        },
        fetchOptions: input.fetchOptions,
      }
      return getWithQuery<{ user: AthenaAuthUser; status: boolean }>(
        '/verify-email',
        queryInput,
        options,
      )
    },
    sendVerificationEmail: (input, options) => postGeneric('/send-verification-email', input, options),
    changeEmail: (input, options) => postGeneric('/change-email', input, options),
    changeEmailVerify: (input, options) => getWithQuery('/change-email/verify', input, options),
    deleteUserVerify: (input, options) => getWithQuery('/delete-user/verify', input, options),
    changePassword: (input, options) => postGeneric('/change-password', input, options),
    user: {
      update: (input, options) => postGeneric('/update-user', input, options),
      delete: (input, options) => postGeneric('/delete-user', input, options),
      email: {
        list: listUserEmailsWithFallback,
      },
    },
    session: {
      list: (input, options) => getGeneric('/list-sessions', input, options),
      revoke: sessionRevokeBinding,
      revokeOther: revokeOtherSessions,
    },
    social: {
      link: (input, options) => postGeneric('/link-social', input, options),
    },
    account: {
      list: (input, options) => getGeneric('/list-accounts', input, options),
      unlink: (input, options) => postGeneric('/unlink-account', input, options),
    },
    deleteUser: {
      callback: deleteUserCallback,
    },
    refreshToken: (input, options) => postGeneric('/refresh-token', input, options),
    getAccessToken: (input, options) => postGeneric('/get-access-token', input, options),
    health: healthWithFallback,
    ok: (input, options) => getGeneric('/ok', input, options),
    error: (input, options) => getGeneric('/error', input, options),
    twoFactor: {
      getTotpUri: (input, options) => postGeneric('/two-factor/get-totp-uri', input, options),
      verifyTotp: (input, options) => postGeneric('/two-factor/verify-totp', input, options),
      sendOtp: (input, options) => postGeneric('/two-factor/send-otp', input, options),
      verifyOtp: (input, options) => postGeneric('/two-factor/verify-otp', input, options),
      verifyBackupCode: (input, options) => postGeneric('/two-factor/verify-backup-code', input, options),
      generateBackupCodes: (input, options) =>
        executePostWithCompatibleInput<
          AthenaTwoFactorGenerateBackupCodesRequest & AthenaAuthFetchCompatibleInput,
          AthenaTwoFactorGenerateBackupCodesResponse
        >(
          resolvedConfig,
          { endpoint: '/two-factor/generate-backup-codes', method: 'POST' },
          input,
          options,
        ),
      enable: (input, options) => postGeneric('/two-factor/enable', input, options),
      disable: (input, options) => postGeneric('/two-factor/disable', input, options),
    },
    passkey: {
      generateRegisterOptions: (input, options) =>
        getGeneric('/passkey/generate-register-options', input, options),
      generateAuthenticateOptions: (input, options) =>
        postGeneric('/passkey/generate-authenticate-options', input, options),
      verifyRegistration: (input, options) =>
        postGeneric('/passkey/verify-registration', input, options),
      verifyAuthentication: (input, options) =>
        postGeneric('/passkey/verify-authentication', input, options),
      listUserPasskeys: (input, options) => getGeneric('/passkey/list-user-passkeys', input, options),
      deletePasskey: (input, options) => postGeneric('/passkey/delete-passkey', input, options),
      updatePasskey: (input, options) => postGeneric('/passkey/update-passkey', input, options),
      getRelatedOrigins: (input, options) => getGeneric('/.well-known/webauthn', input, options),
    },
    admin: {
      role: {
        set: (input, options) => postGeneric('/admin/set-role', input, options),
      },
      user: {
        list: (input, options) => getWithQuery('/admin/list-users', input, options),
        create: (input, options) => postGeneric('/admin/create-user', input, options),
        unban: (input, options) => postGeneric('/admin/unban-user', input, options),
        ban: (input, options) => postGeneric('/admin/ban-user', input, options),
        impersonate: (input, options) => postGeneric('/admin/impersonate-user', input, options),
        stopImpersonating: (input, options) => postGeneric('/admin/stop-impersonating', input, options),
        remove: (input, options) => postGeneric('/admin/remove-user', input, options),
        setPassword: (input, options) => postGeneric('/admin/set-user-password', input, options),
        session: {
          list: (input, options) => postGeneric('/admin/list-user-sessions', input, options),
          revoke: adminUserSessionRevokeBinding,
        },
      },
      hasPermission: (input, options) =>
        postGeneric<AthenaAdminHasPermissionResponse>('/admin/has-permission', input, options),
      requirePermission: (input, options) =>
        requirePermission('/admin/has-permission', input, options),
      apiKey: {
        create: (input, options) => postGeneric('/admin/api-key/create', input, options),
      },
      athenaClient: {
        create: (input, options) => postGeneric('/admin/athena-client/create', input, options),
        list: (input, options) => getWithQuery('/admin/athena-client/list', input, options),
      },
      auditLog: {
        list: (input, options) => getWithQuery('/admin/audit-log/list', input, options),
      },
      email: {
        list: (input, options) => getWithQuery('/admin/email/list', input, options),
        get: (input, options) => getWithQuery('/admin/email/get', input, options),
        create: async (input, options) =>
          postGeneric(
            '/admin/email/create',
            await resolveAdminEmailPayload('/admin/email/create', input),
            options,
          ),
        update: async (input, options) =>
          postGeneric(
            '/admin/email/update',
            await resolveAdminEmailPayload('/admin/email/update', input),
            options,
          ),
        delete: (input, options) => postGeneric('/admin/email/delete', input, options),
        failure: {
          list: (input, options) => getWithQuery('/admin/email-failure/list', input, options),
          get: (input, options) => getWithQuery('/admin/email-failure/get', input, options),
          create: (input, options) => postGeneric('/admin/email-failure/create', input, options),
          update: (input, options) => postGeneric('/admin/email-failure/update', input, options),
          delete: (input, options) => postGeneric('/admin/email-failure/delete', input, options),
        },
        template: {
          list: (input, options) => getWithQuery('/admin/email-template/list', input, options),
          get: (input, options) => getWithQuery('/admin/email-template/get', input, options),
          create: async (input, options) =>
            postGeneric(
              '/admin/email-template/create',
              await resolveAdminEmailTemplatePayload('/admin/email-template/create', input),
              options,
            ),
          update: async (input, options) =>
            postGeneric(
              '/admin/email-template/update',
              await resolveAdminEmailTemplatePayload('/admin/email-template/update', input),
              options,
            ),
          delete: (input, options) => postGeneric('/admin/email-template/delete', input, options),
          send: (input, options) =>
            postGeneric(
              '/admin/email-template/send',
              normalizeAdminEmailTemplateSendPayload(extractFetchOptions(input).payload as Record<string, unknown>),
              options,
            ),
        },
        eventType: {
          list: (input, options) => getWithQuery('/admin/email-event-type/list', input, options),
        },
      },
      emailTemplate: {
        get: (input, options) => getWithQuery('/admin/email-template/get', input, options),
        create: async (input, options) =>
          postGeneric(
            '/admin/email-template/create',
            await resolveAdminEmailTemplatePayload('/admin/email-template/create', input),
            options,
          ),
        delete: (input, options) => postGeneric('/admin/email-template/delete', input, options),
        list: (input, options) => getWithQuery('/admin/email-template/list', input, options),
        update: async (input, options) =>
          postGeneric(
            '/admin/email-template/update',
            await resolveAdminEmailTemplatePayload('/admin/email-template/update', input),
            options,
          ),
        send: (input, options) =>
          postGeneric(
            '/admin/email-template/send',
            normalizeAdminEmailTemplateSendPayload(extractFetchOptions(input).payload as Record<string, unknown>),
            options,
          ),
      },
      emailEventType: {
        list: (input, options) => getWithQuery('/admin/email-event-type/list', input, options),
      },
    },
    apiKey: {
      create: (input, options) => postGeneric('/api-key/create', input, options),
      get: (input, options) => getWithQuery('/api-key/get', input, options),
      update: (input, options) => postGeneric('/api-key/update', input, options),
      delete: (input, options) => postGeneric('/api-key/delete', input, options),
      list: (input, options) => getWithQuery('/api-key/list', input, options),
      verify: (input, options) => postGeneric('/api-key/verify', input, options),
      deleteAllExpired: (input, options) =>
        executePostWithOptionalInput<AthenaApiKeyDeleteAllExpiredResponse>(
          resolvedConfig,
          { endpoint: '/api-key/delete-all-expired-api-keys', method: 'POST' },
          input,
          options,
        ),
    },
    signIn: {
      email: (input, options) => postGeneric('/sign-in/email', input, options),
      username: (input, options) => postGeneric('/sign-in/username', input, options),
      social: (input, options) => postGeneric('/sign-in/social', input, options),
    },
    signUp: {
      email: (input, options) => postGeneric('/sign-up/email', input, options),
    },
    organization,
    callback: {
      provider: (input, options) => {
        const { payload, fetchOptions } = extractFetchOptions(input)
        const parsed = payload as {
          provider?: string
          code?: string
          state?: string
        } | undefined
        const provider = String(parsed?.provider ?? '').trim()
        if (!provider) {
          throw new Error('callback.provider requires a non-empty provider value')
        }
        const code = String(parsed?.code ?? '').trim()
        const state = String(parsed?.state ?? '').trim()
        if (!code || !state) {
          throw new Error('callback.provider requires non-empty code and state values')
        }
        const endpoint = `/callback/${encodeURIComponent(provider)}` as AthenaAuthEndpointPath
        return request({
          endpoint,
          method: 'GET',
          query: {
            code,
            state,
          },
          fetchOptions,
        }, options)
      },
    },
  }

  return {
    baseUrl: normalizedBaseUrl,
    request,
    signIn: {
      email: (
        input: AthenaEmailSignInRequest & AthenaAuthFetchCompatibleInput,
        options?: AthenaAuthCallOptions,
      ) =>
        executePostWithCompatibleInput<
          AthenaEmailSignInRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthSignInResponse
        >(
          resolvedConfig,
          { endpoint: '/sign-in/email', method: 'POST' },
          input,
          options,
        ),
      username: (
        input: AthenaUsernameSignInRequest & AthenaAuthFetchCompatibleInput,
        options?: AthenaAuthCallOptions,
      ) =>
        executePostWithCompatibleInput<
          AthenaUsernameSignInRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthSignInResponse
        >(
          resolvedConfig,
          { endpoint: '/sign-in/username', method: 'POST' },
          input,
          options,
        ),
      social: (
        input: AthenaSocialSignInRequest & AthenaAuthFetchCompatibleInput,
        options?: AthenaAuthCallOptions,
      ) =>
        executePostWithCompatibleInput<
          AthenaSocialSignInRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthSocialRedirectResponse | AthenaAuthSignInResponse
        >(
          resolvedConfig,
          { endpoint: '/sign-in/social', method: 'POST' },
          input,
          options,
        ),
    },
    signUp: {
      email: (
        input: AthenaEmailSignUpRequest & AthenaAuthFetchCompatibleInput,
        options?: AthenaAuthCallOptions,
      ) =>
        executePostWithCompatibleInput<
          AthenaEmailSignUpRequest & AthenaAuthFetchCompatibleInput,
          AthenaAuthSignInResponse
        >(
          resolvedConfig,
          { endpoint: '/sign-up/email', method: 'POST' },
          input,
          options,
        ),
    },
    signOut,
    logout: signOut,
    getSession: (input?: AthenaAuthFetchCompatibleInput, options?: AthenaAuthCallOptions) =>
      executeGetWithCompatibleInput<AthenaAuthSessionResponse>(
        resolvedConfig,
        { endpoint: '/get-session', method: 'GET' },
        input,
        options,
      ),
    getUser,
    requireSession,
    listSessions: (input?: AthenaAuthFetchCompatibleInput, options?: AthenaAuthCallOptions) =>
      executeGetWithCompatibleInput<AthenaAuthSession[]>(
        resolvedConfig,
        { endpoint: '/list-sessions', method: 'GET' },
        input,
        options,
      ),
    revokeSession,
    clearSession: revokeSession,
    revokeSessions,
    clearSessions: revokeSessions,
    revokeOtherSessions,
    clearOtherSessions: revokeOtherSessions,
    forgetPassword: (
      input: AthenaForgetPasswordRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaForgetPasswordRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/forget-password', method: 'POST' },
        input,
        options,
      ),
    resetPassword: (
      input: AthenaResetPasswordRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaResetPasswordRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/reset-password', method: 'POST' },
        input,
        options,
      ),
    resolveResetPasswordToken,
    verifyEmail: (
      input: AthenaVerifyEmailRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) => {
      const { payload, fetchOptions } = extractFetchOptions(input)
      const mergedOptions = mergeCallOptions(fetchOptions, options)
      const query = payload as AthenaVerifyEmailRequest | undefined
      return callAuthEndpoint<{ user: AthenaAuthUser; status: boolean }>(
        resolvedConfig,
        { endpoint: '/verify-email', method: 'GET' },
        undefined,
        query
          ? {
              token: query.token,
              callbackURL: query.callbackURL,
            }
          : undefined,
        mergedOptions,
      )
    },
    sendVerificationEmail: (
      input: AthenaSendVerificationEmailRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaSendVerificationEmailRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/send-verification-email', method: 'POST' },
        input,
        options,
      ),
    changeEmail: (
      input: AthenaChangeEmailRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaChangeEmailRequest & AthenaAuthFetchCompatibleInput,
        { status: boolean; message?: string | null }
      >(
        resolvedConfig,
        { endpoint: '/change-email', method: 'POST' },
        input,
        options,
      ),
    changePassword: (
      input: AthenaChangePasswordRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaChangePasswordRequest & AthenaAuthFetchCompatibleInput,
        { token?: string | null; user: AthenaAuthUser }
      >(
        resolvedConfig,
        { endpoint: '/change-password', method: 'POST' },
        input,
        options,
      ),
    updateUser: (
      input: AthenaUpdateUserRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaUpdateUserRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/update-user', method: 'POST' },
        input,
        options,
      ),
    deleteUser,
    deleteUserCallback,
    linkSocial: (
      input: AthenaLinkSocialRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaLinkSocialRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthSocialRedirectResponse
      >(
        resolvedConfig,
        { endpoint: '/link-social', method: 'POST' },
        input,
        options,
      ),
    listAccounts: (input?: AthenaAuthFetchCompatibleInput, options?: AthenaAuthCallOptions) =>
      executeGetWithCompatibleInput<AthenaAuthLinkedAccount[]>(
        resolvedConfig,
        { endpoint: '/list-accounts', method: 'GET' },
        input,
        options,
      ),
    unlinkAccount: (
      input: AthenaUnlinkAccountRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaUnlinkAccountRequest & AthenaAuthFetchCompatibleInput,
        AthenaAuthStatusResponse
      >(
        resolvedConfig,
        { endpoint: '/unlink-account', method: 'POST' },
        input,
        options,
      ),
    refreshToken: (
      input: AthenaOAuthAccountTokenRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaOAuthAccountTokenRequest & AthenaAuthFetchCompatibleInput,
        AthenaOAuthTokenBundle
      >(
        resolvedConfig,
        { endpoint: '/refresh-token', method: 'POST' },
        input,
        options,
      ),
    getAccessToken: (
      input: AthenaOAuthAccountTokenRequest & AthenaAuthFetchCompatibleInput,
      options?: AthenaAuthCallOptions,
    ) =>
      executePostWithCompatibleInput<
        AthenaOAuthAccountTokenRequest & AthenaAuthFetchCompatibleInput,
        AthenaOAuthTokenBundle
      >(
        resolvedConfig,
        { endpoint: '/get-access-token', method: 'POST' },
        input,
        options,
      ),
    organization,
    auth,
  }
}
